/**
 * THE ASKS BEHIND HER SETTINGS, AS THE OFFICE READS THEM BEFORE IT DECIDES (ops #76 item 5;
 * `docs/seams/home.md` section 12.4). She asked through home to pause, resume, change or end her
 * standing cleans; this is what the office's screen reads of the ask against what the home has now,
 * and the words it drafts for the office to approve or edit.
 *
 * NOTHING HERE IS THE CONTROL. The decision door reads the same ask against the same schedule and
 * refuses what is not right (`decidedOnce` in `api/src/seams/home-requests.ts`): a decision other than
 * what she asked carried as her own request, `not_as_asked`; a change with neither words she will read
 * nor a call, `message_needed`; a price in the words other than hers. What this reads only lets the
 * screen say so before the press, in the same terms.
 *
 * THE WORDS ARE A DRAFT AND THE OFFICE'S. The screen fills the box from the decision's own figures,
 * the day, the weekday, the arrival window and her price of one clean read off the card or the
 * agreement and never typed, and the office approves or edits them, as every message it approves.
 * Nothing writes them but a person (CLAUDE.md, not being built: any AI writing text a client reads).
 */
import {
  BUSINESS_PHONE_DISPLAY,
  CADENCE_LABELS,
  OPT_OUT_LINE,
  SERVICE_LINE_LABELS,
  type Cadence,
  type HomeRequestKind,
  type ServiceLine,
} from '@fk/shared';
import type { AgreementList, AgreementRow, SeriesRow } from './api.ts';
import { addDays, daysBetween, formatLocalWindow, isDay, longDay, weekdayName } from './format.ts';

/** The four asks about a home's standing schedule, each decided by the office. */
export const SCHEDULE_ASK_KINDS: ReadonlySet<HomeRequestKind> = new Set([
  'schedule.pause',
  'schedule.resume',
  'schedule.change',
  'service.end',
]);

/** The four asks about a person on the household, each decided by the office. */
export const PEOPLE_ASK_KINDS: ReadonlySet<HomeRequestKind> = new Set([
  'person.contact_change',
  'person.remove',
  'person.change',
  'person.leave',
]);

/** The days of the week as section 12.4 names them, Monday first. */
export const WEEKDAY_WORDS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const;
export type WeekdayWord = (typeof WEEKDAY_WORDS)[number];

const isWeekdayWord = (value: unknown): value is WeekdayWord =>
  typeof value === 'string' && (WEEKDAY_WORDS as readonly string[]).includes(value);

/** The weekday a day falls on, in the ask's own word: `tuesday`. */
export function weekdayWordOf(day: string): WeekdayWord {
  const word = weekdayName(day).toLowerCase();
  return isWeekdayWord(word) ? word : 'monday';
}

/** "Tuesdays". */
export function weekdaysWord(word: WeekdayWord): string {
  return `${word.charAt(0).toUpperCase()}${word.slice(1)}s`;
}

/** The first day on or after `day` that is a `weekday`, as the decision door reads it. */
export function firstOnOrAfter(day: string, weekday: WeekdayWord): string {
  const target = WEEKDAY_WORDS.indexOf(weekday);
  const current = WEEKDAY_WORDS.indexOf(weekdayWordOf(day));
  return addDays(day, (target - current + 7) % 7);
}

/** A field of her payload as she sent it, or null. */
export function payloadText(
  payload: Readonly<Record<string, unknown>>,
  key: string,
): string | null {
  const value = payload[key];
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/** A day she named, or null when the field is not one. */
function payloadDay(payload: Readonly<Record<string, unknown>>, key: string): string | null {
  const value = payloadText(payload, key);
  return value !== null && isDay(value) ? value : null;
}

/**
 * WHAT THE HOME HAS NOW: the live agreement of the service the ask is about, `house_cleaning` for the
 * three `schedule.*` asks, and its live series. Null where none stands, which the decision door answers
 * by cancelling her ask, `schedule_ended`.
 */
export interface Standing {
  readonly agreement: AgreementRow;
  readonly series: SeriesRow | null;
}

export function standingOf(list: AgreementList, service: ServiceLine): Standing | null {
  const agreement =
    list.agreements.find(
      (row) => row.serviceLine === service && (row.state === 'active' || row.state === 'paused'),
    ) ?? null;
  if (agreement === null) return null;
  const series =
    list.series.find((row) => row.agreementId === agreement.id && row.endedOn === null) ?? null;
  return { agreement, series };
}

/** The service an ask is about: the one named on `service.end`, house cleaning for the others. */
export function serviceOfAsk(kind: HomeRequestKind, payload: Readonly<Record<string, unknown>>) {
  const named = payloadText(payload, 'service');
  return kind === 'service.end' && named !== null && named in SERVICE_LINE_LABELS
    ? (named as ServiceLine)
    : 'house_cleaning';
}

/** Whole dollars as "$199", and anything else to the cent, as her price is said in a message. */
export function dollars(cents: number): string {
  const whole = Math.floor(Math.abs(cents) / 100);
  const rest = Math.abs(cents) % 100;
  return `${cents < 0 ? '-' : ''}$${whole}${rest === 0 ? '' : `.${String(rest).padStart(2, '0')}`}`;
}

/** "Every 2 weeks on Tuesdays, 9:00 am to 1:00 pm, $212 a clean", and her pause where one stands. */
export function standingInWords(standing: Standing): string {
  const { agreement, series } = standing;
  const how = CADENCE_LABELS[agreement.cadence];
  const when = series
    ? ` on ${weekdaysWord(weekdayWordOf(series.anchorDate))}, ${formatLocalWindow(series.windowStartLocal, series.windowEndLocal)}`
    : '';
  const paused = series?.pausedFrom
    ? `. Paused from ${longDay(series.pausedFrom)}${series.pausedUntil ? ` until ${longDay(series.pausedUntil)}` : ', with no return date yet'}`
    : '';
  return `${how}${when}, ${dollars(agreement.priceCents)} a clean${paused}.`;
}

/**
 * HER ASK IN WORDS, as the office reads it beside what she has now: the days she named, and for a
 * change the cadence, the weekday and the hours she asked for, each kept as it is where she named none.
 */
export function askedInWords(
  kind: HomeRequestKind,
  payload: Readonly<Record<string, unknown>>,
): string | null {
  const from = payloadDay(payload, 'from');
  switch (kind) {
    case 'schedule.pause': {
      if (from === null) return null;
      const until = payloadDay(payload, 'until');
      return `A pause from ${longDay(from)}${until ? ` until ${longDay(until)}` : ', with no return date yet'}.`;
    }
    case 'schedule.resume':
      return from === null ? null : `Their cleans back from ${longDay(from)}.`;
    case 'schedule.change': {
      if (from === null) return null;
      const cadence = payloadText(payload, 'cadence') as Cadence | null;
      const weekday = payload.weekday;
      const parts = [
        cadence && cadence in CADENCE_LABELS
          ? CADENCE_LABELS[cadence].toLowerCase()
          : 'the same cadence',
        isWeekdayWord(weekday) ? `on ${weekdaysWord(weekday)}` : 'on the same day',
      ];
      const window = payloadText(payload, 'window');
      return `From ${longDay(from)}: ${parts.join(' ')}${window ? `, at the times they wrote` : ''}.`;
    }
    case 'service.end': {
      const last = payloadDay(payload, 'last_day');
      if (last === null) return null;
      return `${SERVICE_LINE_LABELS[serviceOfAsk(kind, payload)]} to end after ${longDay(last)}.`;
    }
    default:
      return null;
  }
}

/**
 * WHAT A `schedule.change` ASKED, against what she has: her cadence, or the one she has where she named
 * none; her weekday, or the one she has; her `from`; and the first clean on the first day on or after
 * it of that weekday. As the decision door reads it (`scheduleAsked`).
 */
export interface ChangeAsked {
  readonly from: string;
  readonly cadence: Cadence;
  readonly weekday: WeekdayWord;
  readonly anchor: string;
  /** Whether she asked for another cadence or another day, rather than other hours alone. */
  readonly moves: boolean;
}

export function changeAsked(
  payload: Readonly<Record<string, unknown>>,
  standing: Standing,
): ChangeAsked | null {
  const from = payloadDay(payload, 'from');
  if (from === null || standing.series === null) return null;
  const cadenceNow = standing.agreement.cadence;
  const weekdayNow = weekdayWordOf(standing.series.anchorDate);
  const named = payloadText(payload, 'cadence');
  const cadence = named && named in CADENCE_LABELS ? (named as Cadence) : cadenceNow;
  const weekday = isWeekdayWord(payload.weekday) ? payload.weekday : weekdayNow;
  return {
    from,
    cadence,
    weekday,
    anchor: firstOnOrAfter(from, weekday),
    moves: cadence !== cadenceNow || weekday !== weekdayNow,
  };
}

/**
 * WHETHER A CHANGE AS DECIDED IS EXACTLY WHAT SHE ASKED, the door's own reading: a new cadence or a new
 * day must take over from her `from`, at her cadence, with the first clean on her day; the hours alone
 * are what she asked only where she asked for no new cadence and no new day, since her hours are
 * words the office reads into a window, which nothing here can compare.
 */
export function changeAsAsked(
  asked: ChangeAsked,
  standing: Standing,
  decided: { readonly fromDate: string; readonly cadence: Cadence; readonly anchorDate: string },
): boolean {
  const cadenceNow = standing.agreement.cadence;
  const anchor =
    decided.anchorDate !== ''
      ? decided.anchorDate
      : decided.cadence !== cadenceNow
        ? firstOnOrAfter(decided.fromDate, asked.weekday)
        : null;
  if (anchor === null) return !asked.moves;
  return (
    decided.cadence === asked.cadence && decided.fromDate === asked.from && anchor === asked.anchor
  );
}

/**
 * THE FIRST CLEAN NEW HOURS REACH: the first slot of the schedule after the last one already on the
 * calendar, since a clean written keeps its own hours until it is moved through its own page.
 */
export function firstUnwritten(series: SeriesRow): string {
  const last = series.materialisedTo;
  if (last === null) return series.anchorDate;
  const gone = daysBetween(series.anchorDate, last);
  if (gone === null || gone < 0) return series.anchorDate;
  return addDays(
    series.anchorDate,
    (Math.floor(gone / series.intervalDays) + 1) * series.intervalDays,
  );
}

const greeting = (firstName: string | null) =>
  `${firstName ? `Hi ${firstName}, ` : 'Hi, '}this is Faithfully Kept.`;
const closing = `If that does not work for you, call or text ${BUSINESS_PHONE_DISPLAY}.`;
/** A text ends with the opt-out line, as every text the business composes does (ops #23). */
const ending = (sentence: string, channel: 'sms' | 'email') =>
  channel === 'sms' ? `${sentence} ${OPT_OUT_LINE}` : sentence;

/** Words that already tell her how to opt out, read as the composer's door reads them. */
const OPT_OUT_WORDS = 'stop to opt out';

/**
 * WORDS THE OFFICE EDITED, MOVED TO ANOTHER CHANNEL (the review's C11-4). The draft is redrafted on a
 * change of channel, but edited words are kept as typed, so the opt-out line a text ops offers ends
 * with (`docs/seams/phone.md`, `body`) went with them only if they were edited on a text. Moved to a
 * text, words that do not already say how to opt out gain the line; moved to an email, which carries
 * none, a line at their end comes off. Anything else the office wrote is left as it wrote it, and the
 * office may still delete the line: the phone system's check when the text goes is the control.
 */
export function wordsOnChannel(body: string, channel: 'sms' | 'email'): string {
  if (channel === 'sms') {
    return body.toLowerCase().includes(OPT_OUT_WORDS) ? body : `${body.trimEnd()} ${OPT_OUT_LINE}`;
  }
  const trimmed = body.trimEnd();
  return trimmed.endsWith(OPT_OUT_LINE)
    ? trimmed.slice(0, trimmed.length - OPT_OUT_LINE.length).trimEnd()
    : body;
}

/** Her first name, for the greeting: the first word of her name. */
export function firstNameOf(displayName: string): string | null {
  const first = displayName.trim().split(/\s+/)[0];
  return first ? first : null;
}

/**
 * THE NEWS A CHANGE TO HER STANDING CLEANS IS, drafted from the decision's figures: from which clean,
 * how often, on which day, the arrival window and her price of one clean. For the hours alone, from
 * the first clean not yet on her calendar, the ones before it keeping their times.
 */
export function draftChange(words: {
  readonly firstName: string | null;
  readonly firstClean: string;
  readonly cadence: Cadence;
  readonly weekday: WeekdayWord;
  readonly startLocal: string;
  readonly endLocal: string;
  readonly priceCents: number | null;
  readonly hoursOnly: boolean;
  readonly channel: 'sms' | 'email';
}): string {
  const window = formatLocalWindow(words.startLocal, words.endLocal);
  const price = words.priceCents === null ? '' : `, at ${dollars(words.priceCents)} a clean`;
  const body = words.hoursOnly
    ? `From ${longDay(words.firstClean)} your cleans on ${weekdaysWord(words.weekday)} arrive ${window}${price}. The cleans already on your calendar before then keep their times.`
    : `From ${longDay(words.firstClean)} your cleans are ${CADENCE_LABELS[words.cadence].toLowerCase()} on ${weekdaysWord(words.weekday)}, ${window}${price}.`;
  return ending(`${greeting(words.firstName)} ${body} ${closing}`, words.channel);
}

/** The confirmation beside a pause, a resume or an ending, drafted from the decision's days. */
export function draftConfirmation(
  kind: HomeRequestKind,
  words: {
    readonly firstName: string | null;
    readonly from: string;
    readonly until: string | null;
    readonly service: ServiceLine;
    readonly channel: 'sms' | 'email';
  },
): string {
  const body =
    kind === 'schedule.pause'
      ? `Your regular cleans are paused from ${longDay(words.from)}${words.until ? ` until ${longDay(words.until)}` : ' until you tell us you are ready to start again'}.`
      : kind === 'schedule.resume'
        ? `Your regular cleans start again from ${longDay(words.from)}.`
        : `Your ${SERVICE_LINE_LABELS[words.service].toLowerCase()} with us ends after ${longDay(words.from)}.`;
  return ending(`${greeting(words.firstName)} ${body} ${closing}`, words.channel);
}
